function Accessibility() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold tracking-tight mb-6 font-serif">Accessibility statement</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Our Commitment</h2>
        <p className="text-muted-foreground leading-relaxed">
          Readwell is committed to making digital reading accessible to every K-12 student.
          Our platform is designed to give districts the documentation and conformance they need
          to procure us under their ADA Title II obligations, and we are continuously closing the
          gap between our current state and full WCAG 2.1 Level AA conformance.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Conformance Status</h2>
        <p className="text-sm text-muted-foreground mb-3">
          <strong className="text-foreground">As of May 3, 2026.</strong>
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          The Web Content Accessibility Guidelines (WCAG) define requirements for designers
          and developers to improve accessibility for people with disabilities. Readwell targets
          <strong className="text-foreground"> WCAG 2.1 Level AA</strong> conformance.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          An internal code-review audit completed April 26, 2026 identified five Critical and
          nine Major findings. As of May 3, 2026 all five Critical and all nine Major
          findings have shipped fixes. Procurement-facing artifacts (VPAT 2.4 Rev WCAG 2.2)
          and third-party assistive-technology validation are in progress; see <strong className="text-foreground">Known Issues</strong> below
          for the current status.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Districts that need an interim conformance summary or the in-progress VPAT for
          procurement can email
          <a href="mailto:accessibility@readwell.academy" className="text-primary hover:underline"> accessibility@readwell.academy</a>.
          We aim to acknowledge accessibility inquiries within 5 business days.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Accessibility Features</h2>

        <h3 className="text-xl font-semibold mb-2">Bimodal Reading</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li>Text synchronized with professional audio narration</li>
          <li>Word-level highlighting shows which word is being spoken</li>
          <li>Auto-scroll keeps the highlighted word in view</li>
          <li>Adjustable playback speed (0.5× to 2.0×)</li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Reading Accommodations</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li>OpenDyslexic font option in the bimodal reader</li>
          <li>Adjustable reading focus (letters-ahead lookahead)</li>
          <li>Light and dark themes</li>
          <li>Honors the operating system <code className="px-1 py-0.5 bg-muted rounded text-xs">prefers-reduced-motion</code> setting</li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Keyboard Navigation</h3>
        <p className="text-sm text-muted-foreground ml-4 mb-3">
          Standard navigation:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Tab</kbd> — move between interactive elements</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Shift</kbd> + <kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Tab</kbd> — move backward</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Enter</kbd> or <kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Space</kbd> — activate buttons and links</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Esc</kbd> — close dialogs and dropdowns</li>
        </ul>
        <p className="text-sm text-muted-foreground ml-4 mb-3">
          Player shortcuts (active when a book is loaded; ignored while typing in a form field
          or when focus is on a button):
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">Space</kbd> or <kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">K</kbd> — play / pause</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">←</kbd> or <kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">J</kbd> — skip back 5 seconds</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">→</kbd> or <kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">L</kbd> — skip forward 5 seconds</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">↑</kbd> — increase playback speed</li>
          <li><kbd className="px-2 py-1 bg-muted text-foreground rounded text-sm font-mono">↓</kbd> — decrease playback speed</li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Screen Reader Support</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li>Semantic HTML structure with heading hierarchy</li>
          <li>ARIA labels on player controls and other interactive elements</li>
          <li>Skip-to-main-content link as the first focusable element on every page</li>
          <li>Live region announces player state changes (loading, buffering, playing, paused)</li>
          <li>Per-route document titles so screen reader users can distinguish tabs</li>
        </ul>
        <p className="text-sm text-muted-foreground ml-4 mb-4">
          Third-party screen-reader validation (NVDA, VoiceOver, TalkBack) is planned. See
          <strong className="text-foreground"> Assessment</strong> below.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Assessment</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          We assess accessibility through:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
          <li>Internal code-review audit against WCAG 2.1 Level AA (most recent: April 26, 2026; remediation phases A, B, and C shipped April 26–28, 2026)</li>
          <li>Manual keyboard navigation testing</li>
          <li>Browser-based color contrast analysis</li>
          <li>Third-party assistive-technology validation — VoiceOver on macOS Safari in progress; TalkBack on Android Chrome and NVDA on Windows Chrome scheduled (see <strong className="text-foreground">Known Issues</strong>)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Known Issues</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          A snapshot of where remediation stands relative to our April 26, 2026 internal audit.
        </p>

        <h3 className="text-xl font-semibold mb-2">Closed since the audit</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li>Primary CTA contrast — failing button color replaced with a darker brand green that meets AA contrast (≥4.5:1)</li>
          <li>Keyboard support — clickable elements converted to real buttons; global player keyboard shortcuts implemented (Space/K, J/L, ←/→, ↑/↓)</li>
          <li>Page titles — every route now sets a unique document title so screen-reader users can distinguish tabs</li>
          <li>Skip-to-main-content link — added as the first focusable element on every page</li>
          <li>Form input contrast — input borders darkened to meet 3:1 non-text contrast</li>
          <li>Focus ring — increased from 1px to 2px with offset; visible on all interactive elements</li>
          <li>Decorative icons — marked <code className="px-1 py-0.5 bg-muted rounded text-xs">aria-hidden</code> to prevent duplicate screen-reader announcements</li>
          <li>Mobile reflow — persistent media controls bar stacks vertically below 768px viewport</li>
          <li>Form labels — student access code inputs and Settings display fields use semantically correct labelling</li>
          <li>Reduced motion — color transitions in the bimodal reader honor <code className="px-1 py-0.5 bg-muted rounded text-xs">prefers-reduced-motion</code></li>
          <li>Accessibility statement (this page) — revised to remove unverified conformance claims</li>
          <li>Dedicated accessibility contact email — <a href="mailto:accessibility@readwell.academy" className="text-primary hover:underline">accessibility@readwell.academy</a> live since April 27, 2026</li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">In progress</h3>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
          <li>VPAT 2.4 Rev WCAG 2.2 — re-grading the conformance map against current code; target publication May 2026</li>
          <li>Third-party assistive-technology validation — VoiceOver on macOS Safari in progress; TalkBack on Android Chrome and NVDA on Windows Chrome scheduled</li>
          <li>Verification of live-region status announcements with a screen reader</li>
        </ul>

        <h3 className="text-xl font-semibold mb-2">Outstanding minor items</h3>
        <p className="text-sm text-muted-foreground ml-4 mb-3">
          Polish-level items from the audit (would map to "Partially Supports" in the VPAT).
          Tracked for a future remediation pass; not blocking AA conformance.
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
          <li>Verify book card titles do not get truncated below 200% browser zoom</li>
          <li>Replace the native browser tooltip on the dyslexia-mode toggle with an accessible tooltip component</li>
          <li>Improve student access code error messages with concrete suggestions</li>
          <li>Verify autocomplete attributes on the district signup form</li>
          <li>Add a non-color cue for the currently-spoken word in the bimodal reader</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Feedback</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          We welcome your feedback on the accessibility of Readwell. If you encounter an
          accessibility barrier, please let us know:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-3">
          <li>Email: <a href="mailto:accessibility@readwell.academy" className="text-primary hover:underline">accessibility@readwell.academy</a></li>
          <li>General contact: <a href="mailto:info@readwell.academy" className="text-primary hover:underline">info@readwell.academy</a></li>
        </ul>
        <p className="text-muted-foreground leading-relaxed">
          We aim to acknowledge accessibility feedback within 5 business days.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Formal Complaints</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          If you are not satisfied with our response to your accessibility concerns,
          you may file a formal complaint with:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
          <li>
            <strong className="text-foreground">U.S. Department of Education, Office for Civil Rights (OCR)</strong>
            <br />
            <a
              href="https://www2.ed.gov/about/offices/list/ocr/complaintintro.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              File a complaint with OCR
            </a>
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Last Updated:</strong> May 3, 2026
        </p>
      </section>
    </div>
  );
}

export default Accessibility;
